import type { OnInit } from '@angular/core';
import {
  Component,
  createNgModule,
  inject,
  Injector,
  Input,
  PendingTasks,
  PLATFORM_ID,
  reflectComponentType,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import type { Observable } from 'rxjs';

import { CmsFormType, CmsViewType, type CmsPlaceholder, type CmsTemplate } from '@fcom/core-api';

type LazyComponentInput = {
  template?: CmsTemplate;
  content$?: Observable<CmsPlaceholder>;
  content?: CmsPlaceholder;
};

@Component({
  selector: 'fin-render-lazy-component',
  templateUrl: './render-lazy-component.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RenderLazyComponent implements OnInit {
  private injector = inject(Injector);
  private platformId = inject(PLATFORM_ID);
  private pendingTasks = inject(PendingTasks);

  @ViewChild('lazyLoadComponent', { read: ViewContainerRef })
  lazyLoadComponent!: ViewContainerRef;

  @Input()
  viewType: CmsViewType | CmsFormType;

  @Input()
  content?: CmsPlaceholder;

  @Input()
  content$?: Observable<CmsPlaceholder>;

  @Input()
  template?: CmsTemplate;

  ngOnInit(): void {
    if (this.viewType === CmsViewType.DESTINATIONS_SEARCH || this.viewType === CmsViewType.DESTINATION_SEARCH_HERO) {
      this.loadDestinationSearch();
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.viewType === CmsViewType.RETRIEVE_PAGE_CONTENT) {
      this.loadRetrievePage();
    } else if (
      Object.keys(CmsFormType)
        .map((key) => CmsFormType[key])
        .includes(this.viewType)
    ) {
      this.loadForm();
    }
  }

  async loadDestinationSearch(): Promise<void> {
    const removePendingTask = this.pendingTasks.add();
    try {
      const { DestinationSearchLazyModule, DestinationSearchPageComponent, DestinationSearchHeroComponent } =
        await import('@fcom/destination-search-lazy');

      const destinationSearchTypeToComponentMap = {
        [CmsViewType.DESTINATIONS_SEARCH]: DestinationSearchPageComponent,
        [CmsViewType.DESTINATION_SEARCH_HERO]: DestinationSearchHeroComponent,
      };
      const moduleRef = createNgModule(DestinationSearchLazyModule, this.injector);

      const componentRef = this.lazyLoadComponent.createComponent<LazyComponentInput>(
        destinationSearchTypeToComponentMap[this.viewType],
        {
          ngModuleRef: moduleRef,
        }
      );

      componentRef.setInput('template', this.template);
      componentRef.setInput('content$', this.content$);
      componentRef.setInput('content', this.content);
    } finally {
      removePendingTask();
    }
  }

  async loadForm(): Promise<void> {
    const {
      CancellationCoverClaimFormComponent,
      CaseAttachmentsFormComponent,
      CertificatesForInsuranceCompanyFormComponent,
      ClaimMissingAviosFormComponent,
      ClaimMissingAviosPartnerFormComponent,
      CorporateContactFormComponent,
      GiftCardInformationFormComponent,
      JoinCorporateFormComponent,
      RefundFormComponent,
      RefundStatusCheckerFormComponent,
      SafDownloadFormComponent,
      ServiceFormsLazyModule,
      SpecialAssistanceFormComponent,
      SpecialAssistanceWizardComponent,
      VatReceiptFormComponent,
    } = await import('@fcom/service-forms-lazy');
    const moduleRef = createNgModule(ServiceFormsLazyModule, this.injector);

    const formTypeToComponentMap = {
      [CmsFormType.CANCELLATION_COVER_CLAIM_FORM]: CancellationCoverClaimFormComponent,
      [CmsFormType.CASE_ATTACHMENT_FORM]: CaseAttachmentsFormComponent,
      [CmsFormType.CERTIFICATES_FOR_INSURANCE_COMPANY_FORM]: CertificatesForInsuranceCompanyFormComponent,
      [CmsFormType.CLAIM_MISSING_AVIOS_FORM]: ClaimMissingAviosFormComponent,
      [CmsFormType.CLAIM_MISSING_AVIOS_PARTNER_FORM]: ClaimMissingAviosPartnerFormComponent,
      [CmsFormType.CORPORATE_CONTACT_FORM]: CorporateContactFormComponent,
      [CmsFormType.GIFT_CARD_INFORMATION_FORM]: GiftCardInformationFormComponent,
      [CmsFormType.JOIN_CORPORATE_FORM]: JoinCorporateFormComponent,
      [CmsFormType.REFUND_FORM]: RefundFormComponent,
      [CmsFormType.REFUND_STATUS_CHECKER_FORM]: RefundStatusCheckerFormComponent,
      [CmsFormType.SAF_DOWNLOADABLE_FORM]: SafDownloadFormComponent,
      [CmsFormType.SPECIAL_ASSISTANCE_FORM]: SpecialAssistanceFormComponent,
      [CmsFormType.SPECIAL_ASSISTANCE_WIZARD]: SpecialAssistanceWizardComponent,
      [CmsFormType.VAT_RECEIPT_FORM]: VatReceiptFormComponent,
    };

    if (formTypeToComponentMap[this.viewType]) {
      const componentType = formTypeToComponentMap[this.viewType];
      const componentRef = this.lazyLoadComponent.createComponent<LazyComponentInput>(componentType, {
        ngModuleRef: moduleRef,
      });
      const componentMirror = reflectComponentType(componentType);
      if (componentMirror?.inputs.some((i) => i.propName === 'template')) {
        componentRef.setInput('template', this.template);
      }
    }
  }

  async loadRetrievePage(): Promise<void> {
    const { RetrieveModule, RetrievePageContentComponent } =
      await import('@fcom/service-forms-lazy/components/retrieve-page');

    const moduleRef = createNgModule(RetrieveModule, this.injector);

    this.lazyLoadComponent.createComponent<unknown>(RetrievePageContentComponent, { ngModuleRef: moduleRef });
  }
}
